import { AuditHistoryItem } from '../hooks/useAuditHistory';

// Format audit history date with timezone conversion
// Input: "2026-02-12T14:47:06.059Z" (UTC)
// Output: "Feb 12, 2026, 02:47 PM" (Local timezone)
export const formatAuditHistoryDate = (timestamp: string): string => {
  try {
    // Convert UTC timestamp to local timezone
    const date = new Date(timestamp);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return timestamp;
    }
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const year = date.getFullYear();
    const month = monthNames[date.getMonth()];
    const day = date.getDate();
    let hour = date.getHours();
    const minute = date.getMinutes().toString().padStart(2, '0');
    
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    hour = hour ? hour : 12;
    const hourStr = hour.toString().padStart(2, '0');
    
    return `${month} ${day}, ${year}, ${hourStr}:${minute} ${ampm}`;
  } catch {
    return timestamp;
  }
};

export const formatHeaderDate = (dateString?: string): string => {
  if (!dateString) return '';
  try {
    let year: number, month: number, day: number;
    
    if (dateString.includes('T')) {
      // ISO format with time: YYYY-MM-DDTHH:mm:ss.sssZ
      // Convert to local timezone to get the correct date in user's timezone
      const date = new Date(dateString);
      year = date.getFullYear();
      month = date.getMonth() + 1;
      day = date.getDate();
    } else if (dateString.includes('-')) {
      // Simple format: YYYY-MM-DD (date only, no time)
      // Parse directly without timezone conversion to avoid date shift
      const parts = dateString.split('-');
      year = parseInt(parts[0], 10);
      month = parseInt(parts[1], 10);
      day = parseInt(parts[2], 10);
    } else {
      // Fallback: try to parse as-is and use local timezone
      const date = new Date(dateString);
      month = date.getMonth() + 1;
      day = date.getDate();
      year = date.getFullYear();
    }
    
    // Convert month number to short name
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthName = monthNames[month - 1];
    
    return `${monthName} ${day}, ${year}`;
  } catch {
    return dateString;
  }
};

export const getActionTypeLabel = (
  actionType: string
): string => {
  // normalize backend action types to canonical keys used in labels
  const normalized =
    actionType === 'ASSIGNMENT_DECLINED' ? 'DECLINED' :
    actionType === 'ASSIGNMENT_ACCEPTED' ? 'ACCEPTED' :
    actionType === 'ASSIGNMENT_COMPLETED' ? 'COMPLETED' : actionType;

  const labels: Record<string, string> = {
    CREATED: 'Created',
    UPDATED: 'Updated',
    DECLINED: 'Declined',
    ACCEPTED: 'Accepted',
    COMPLETED: 'Completed',
  };
  return labels[normalized] || actionType;
};

export const parseJsonValue = (jsonString: string | null): any => {
  if (!jsonString) return null;
  try {
    return JSON.parse(jsonString);
  } catch {
    return null;
  }
};

export const formatPerformedByName = (
  gpid: string,
  name?: string,
  email?: string
): string => {
 
  if (name) {
   
    if (name.includes(',')) {
      const parts = name.split(',').map(p => p.trim());
      if (parts.length === 2) {
        return `${parts[1]} ${parts[0]}`;
      }
    }
   
    return name;
  }

  if (email) {
    const nameFromEmail = email.split('@')[0] ?? '';
    const nameParts = nameFromEmail.split('.');
    
    const firstAndLastName = nameParts.slice(0, 2);
    
    const displayName = firstAndLastName
      .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
    
    return displayName.replace(/\s*Contractor\s*$/i, '').trim() || email;
  }

  return gpid;
};

export const getActionDescription = (item: AuditHistoryItem): string => {
  const performedByName = formatPerformedByName(
    item.PERFORMED_BY_GPID,
    item.PERFORMED_BY_NAME,
    item.PERFORMED_BY_EMAIL
  );

  // normalize action type from backend (cast to string to avoid TS union comparison errors)
  const rawActionType = String(item.ACTION_TYPE);
  const actionType =
    rawActionType === 'ASSIGNMENT_DECLINED' ? 'DECLINED' :
    rawActionType === 'ASSIGNMENT_ACCEPTED' ? 'ACCEPTED' :
    rawActionType === 'ASSIGNMENT_COMPLETED' ? 'COMPLETED' : rawActionType;

  switch (actionType) {
    case 'CREATED':
      return `Action Plan created by ${performedByName}`;

    case 'UPDATED':
      return `Action Plan updated by ${performedByName}`;

    case 'DECLINED':
      return `Action Plan declined by ${performedByName}`;

    case 'ACCEPTED':
      return `Action Plan accepted by ${performedByName}`;

    case 'COMPLETED':
      return `Action Plan completed by ${performedByName}`;

    default:
      return `Action Plan updated by ${performedByName}`;
  }
};

export interface ActionDescriptionPart {
  text: string;
  bold?: boolean;
}

export const getActionDescriptionParts = (item: AuditHistoryItem): ActionDescriptionPart[] => {
  const performedByName = formatPerformedByName(
    item.PERFORMED_BY_GPID,
    item.PERFORMED_BY_NAME,
    item.PERFORMED_BY_EMAIL
  );

  // normalize action type from backend (cast to string to avoid TS union comparison errors)
  const rawActionType = String(item.ACTION_TYPE);
  const actionType =
    rawActionType === 'ASSIGNMENT_DECLINED' ? 'DECLINED' :
    rawActionType === 'ASSIGNMENT_ACCEPTED' ? 'ACCEPTED' :
    rawActionType === 'ASSIGNMENT_COMPLETED' ? 'COMPLETED' : rawActionType;

  switch (actionType) {
    case 'CREATED':
      return [
        { text: 'Action Plan', bold: true },
        { text: ' created by ' },
        { text: performedByName, bold: true },
      ];

    case 'UPDATED':
      return [
        { text: 'Action Plan', bold: true },
        { text: ' updated by ' },
        { text: performedByName, bold: true },
      ];

    case 'DECLINED':
      return [
        { text: performedByName, bold: true },
        { text: ' declined Action Plan' },
      ];

    case 'ACCEPTED':
      return [
        { text: 'Action Plan', bold: true },
        { text: ' accepted by ' },
        { text: performedByName, bold: true },
      ];

    case 'COMPLETED':
      return [
        { text: performedByName, bold: true },
        { text: ' marked Action plan as ' },
        { text: 'Completed', bold: true },
      ];

    default:
      return [
        { text: 'Action Plan', bold: true },
        { text: ' updated by ' },
        { text: performedByName, bold: true },
      ];
  }
};

export const getStatusBadgeColor = (
  actionType: string
): string => {
  // accept ASSIGNMENT_DECLINED / ASSIGNMENT_ACCEPTED / ASSIGNMENT_COMPLETED as equivalent
  const normalized =
    actionType === 'ASSIGNMENT_DECLINED' ? 'DECLINED' :
    actionType === 'ASSIGNMENT_ACCEPTED' ? 'ACCEPTED' :
    actionType === 'ASSIGNMENT_COMPLETED' ? 'COMPLETED' : actionType;

  const colors: Record<string, string> = {
    CREATED: '#027C06',
    UPDATED: '#1976D2',
    DECLINED: '#D32F2F',
    ACCEPTED: '#27AE60',
    COMPLETED: '#6A1B9A',
  };
  return colors[normalized] || '#666';
};

export const getChangedFieldsDescription = (
  previousValues: string | null,
  newValues: string | null
): string => {
  try {
    const prev = previousValues ? JSON.parse(previousValues) : {};
    const newVal = newValues ? JSON.parse(newValues) : {};

    const changedFields: string[] = [];

    Object.keys(newVal).forEach((key) => {
      if (prev[key] !== newVal[key]) {
        const fieldName = key
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
        changedFields.push(fieldName);
      }
    });

    return changedFields.join(', ');
  } catch {
    return 'Multiple fields';
  }
};

export const formatStatusDisplay = (status: string): string => {
  if (!status) return '';
  let formatted = status.replace(/_/g, ' ');
  formatted = formatted
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
  
  return formatted;
};
